import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
    PLATFORM_ID,
    ViewEncapsulation,
    DOCUMENT
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
    ANC_SIDE_PANEL_DEFAULT_WIDTHS,
    AncSidePanelVariant
} from './anc-side-panel-container.model';
import { AncPageHeaderComponent } from '../anc-page-header/anc-page-header.component';
import { AncViewportService } from '../../services/anc-viewport.service';

/**
 * Right-aligned side panel shell with overlay scrim (Figma Create Order Form).
 * Provides variant-based width presets (`forms` = 1080px, `details` = 70vw)
 * and a single content projection slot. Consumers own header, footer, and scroll.
 * Apply class `anc-side-panel__content` on elements inside the scroll area (and footer)
 * for the default 24px horizontal inset; the scrollbar stays flush with the panel edge.
 *
 * Overlay scrim is always shown. Use {@link closeOnBackdropClick} to allow or block
 * dismiss on outside click; close/cancel actions remain in the parent template.
 *
 * @example
 * <lib-anc-side-panel-container
 *   variant="forms"
 *   [title]="'Add New Product'"
 *   [closeOnBackdropClick]="true"
 *   (closeClick)="navigateBack()"
 *   (backdropClick)="navigateBack()"
 * >
 *   <ng-scrollbar>...</ng-scrollbar>
 * </lib-anc-side-panel-container>
 */
@Component({
    selector: 'lib-anc-side-panel-container',
    imports: [AncPageHeaderComponent],
    templateUrl: './anc-side-panel-container.component.html',
    styleUrls: ['./anc-side-panel-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'anc-side-panel-container-host',
        '[style.z-index]': 'zIndex() ?? 1010'
    }
})
export class AncSidePanelContainerComponent {
    private readonly hostRef = inject(ElementRef<HTMLElement>);
    private readonly document = inject(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly destroyRef = inject(DestroyRef);
    private readonly viewport = inject(AncViewportService);

    private anchorParent: Node | null = null;
    private anchorSibling: Node | null = null;

    /** Layout preset controlling default panel width. */
    variant = input<AncSidePanelVariant>('forms');

    /** Explicit width override (e.g. `'920px'`, `'70vw'`). Takes precedence over variant. */
    width = input<string | null>(null);

    /** Panel surface background. Defaults to customer white-label token. */
    background = input<string>('var(--new-bg-white-color, #ffffff)');

    /** Full-viewport scrim override. Falls back to `--shipping-color` / `--overlay` when omitted. */
    overlayBackground = input<string | undefined>(undefined);

    /**
     * When true, outside (scrim) click emits {@link backdropClick}.
     * When false, dismiss only via explicit parent actions (close/cancel buttons).
     */
    closeOnBackdropClick = input<boolean>(true);

    /** Emitted on scrim click when {@link closeOnBackdropClick} is true. */
    readonly backdropClick = output<void>();

    /** Stacking order override. Falls back to `1010` when omitted. */
    zIndex = input<number | undefined>(undefined);

    /** Optional page title; renders {@link AncPageHeaderComponent} when non-empty. */
    title = input<string>('');

    /** When false, page header omits its bottom border. */
    titleBordered = input<boolean>(true);

    /** Emitted when the user clicks the page-header close button. */
    readonly closeClick = output<void>();

    /**
     * Resolved panel width from override or variant default. Below 840px (OEM-7023,
     * FR-060) every panel takes the full width; wider overrides are also capped by
     * the panel's `max-width: 100%`.
     */
    readonly resolvedWidth = computed(() => {
        const viewportClass = this.viewport.viewportClass();
        if (viewportClass === 'mobile' || viewportClass === 'compact') {
            return '100%';
        }
        const width = this.width() ?? ANC_SIDE_PANEL_DEFAULT_WIDTHS[this.variant()];
        // Landscape tablets (840–1199px, OEM-7023): a 1080px forms panel would fill the whole
        // screen; keep part of the page visible on the left, as on desktop (e.g. ~820px at
        // 1024). Narrower requested widths (e.g. details 70vw) are unaffected.
        if (viewportClass === 'expanded') {
            return `min(${width}, max(80vw, 720px))`;
        }
        return width;
    });

    constructor() {
        afterNextRender(() => {
            if (!isPlatformBrowser(this.platformId)) {
                return;
            }

            const host = this.hostRef.nativeElement;
            this.anchorParent = host.parentNode;
            this.anchorSibling = host.nextSibling;
            this.document.body.appendChild(host);

            this.destroyRef.onDestroy(() => {
                if (
                    host.parentNode !== this.document.body ||
                    !this.anchorParent
                ) {
                    return;
                }

                this.anchorParent.insertBefore(host, this.anchorSibling);
            });
        });
    }

    onBackdropClick(event: MouseEvent): void {
        if (!this.closeOnBackdropClick()) {
            return;
        }

        if (event.target !== event.currentTarget) {
            return;
        }

        this.backdropClick.emit();
    }
}
